import { useCallback } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { SendRequestAfterSaving } from "@/components/request-upload-files-dialog"
import { Button } from "@/components/ui/button"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { GraphqlErrors } from "@/lib/utils"
import { submitBtnAgendaTeleconsultationPatientFragment$key } from "@/modules/patients/agenda/teleconsultation/booking/__generated__/submitBtnAgendaTeleconsultationPatientFragment.graphql"
import {
  getAccess,
  getPhoneNumber,
  resolveMedgateConnectChannel,
} from "@/modules/patients/agenda/teleconsultation/booking/libs/teleconsultation-helper"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { getPreventionProgramsConnectionId } from "@/modules/patients/detail/health-information/condition/lib/get-prevention-programs-connection"
import { mayFavorNurseWhenAssigningMedicalAgent } from "@/modules/patients/helpers/patient-helper"
import { useAppointmentParam, useIsInContextOfAppointment } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  AddressInput,
  CreateAppointmentInput,
  CreateMedgateConnectOrderInput,
} from "@/modules/patients/services/__generated__/useCreateAppointmentMutation.graphql"
import {
  UpdateAppointmentInput,
  UpdateMedgateConnectOrderInput,
} from "@/modules/patients/services/__generated__/useUpdateAppointmentMutation.graphql"
import { getPatientConnectionId } from "@/modules/patients/services/PatientAppointment"
import { useCreateAppointmentMutation } from "@/modules/patients/services/useCreateAppointmentMutation"
import { useFileUploadRequestMutation } from "@/modules/patients/services/useFileUploadRequestMutation"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import {
  PreventionProgramEngagementInput,
  useSavePreventionProgramEngagementsMutation,
} from "@/modules/patients/services/useSavePreventionProgramEngagementsMutation"
import { useUpdateAppointmentMutation } from "@/modules/patients/services/useUpdateAppointmentMutation"

export const AgendaTeleconsultationPatientFragment = graphql`
  fragment submitBtnAgendaTeleconsultationPatientFragment on Patient {
    phoneNumber
    birthDate
    insurees {
      activeCoverages {
        product {
          productConfiguration {
            nursingRate
          }
        }
      }
    }
  }
`

const SubmitButton = ({
  mode,
  handleCloseDrawer,
  sendRequestAfterSaving,
}: {
  mode: TeleconsultationMode
  handleCloseDrawer: () => void
  sendRequestAfterSaving?: SendRequestAfterSaving
}) => {
  const { t } = useTranslation()

  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientRef] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const countryPatient = patientRef.patientById
  if (!countryPatient) {
    throw new Error("Not Found The Patient")
  }

  const patient = useFragment<submitBtnAgendaTeleconsultationPatientFragment$key>(
    AgendaTeleconsultationPatientFragment,
    countryPatient,
  )
  const { phoneNumber: patientPhoneNumber, birthDate: patientBirthDate } = patient

  const [createAppointment, isCreateFlight] = useCreateAppointmentMutation()
  const [updateAppointment, isUpdateFlight] = useUpdateAppointmentMutation()
  const [requestUploadFile] = useFileUploadRequestMutation()
  const [savePreventionProgramEngagements] = useSavePreventionProgramEngagementsMutation(countryCode)

  const savePreventionEngagements = useCallback(
    (preventionProgramKeys: string[]) => {
      if (!preventionProgramKeys || preventionProgramKeys.length === 0) return

      const engagements: PreventionProgramEngagementInput[] = preventionProgramKeys.map((key) => ({
        preventionProgramKey: key,
        status: "BOOKED",
      }))

      savePreventionProgramEngagements({ patientId, engagements }, [getPreventionProgramsConnectionId(patientId)])
    },
    [patientId, savePreventionProgramEngagements],
  )

  const isInContextOfAppointment = useIsInContextOfAppointment()

  const { handleSubmit, formState, setValue, getValues } = useFormContext<TeleconsultationForm>()
  const { isDirty, isValidating } = formState

  // When the selected slot is no longer available, we need to refetch the available slots
  // This is done by setting the date to the same value with selected one but different object reference,
  // which will trigger the query to refetch
  const refetchAvailableSlots = () => {
    const searchDate = new DateOnly(getValues("date").toDate())
    setValue("date", searchDate)
  }

  const handleBookingErrors = (errors: GraphqlErrors) => {
    if (errors.some((error) => error.__typename === "SlotNotAvailable")) {
      toast({ description: t("patient.agenda.booking.error.SlotNotAvailable") })
      refetchAvailableSlots()
    }
    if (errors.some((error) => error.__typename === "MedicalChatNotEnabled")) {
      toast({ description: t("patient.agenda.booking.error.MedicalChatNotEnabled") })
    }
  }

  const handleSave = (formData: TeleconsultationForm) => {
    if (!isDirty && mode.kind === "EDITING_APPOINTMENT") {
      handleCloseDrawer()
      return
    }

    const {
      id,
      date,
      slotId,
      urgency,
      appointmentType,
      gender,
      agentUserName,
      addressSource,
      country,
      city,
      postalCode,
      houseNumberStreet,
      followUpForProgressNoteId,
      assignedPartner,
      partnerType,
      isPartnerDoc2U,
      ...restFormData
    } = formData

    const dateInput = urgency !== "NORMAL" ? null : date.toString()
    const slotIdInput = urgency !== "NORMAL" ? null : slotId
    const assignedTo = agentUserName
    const desiredAgentGender = (!agentUserName || agentUserName.length === 0) && gender ? gender : "UNKNOWN"
    const patientAddress: AddressInput | null =
      addressSource === "MANUAL" && country && city && postalCode && houseNumberStreet
        ? { countryCode: country, city, postalCode, houseNumberStreet }
        : null
    const followUpForProgressNoteIdInput = appointmentType === "FOLLOW_UP" ? followUpForProgressNoteId : null
    const medgateConnectChannel = resolveMedgateConnectChannel(restFormData.service, addressSource)
    const { phoneNumber } =
      addressSource === "PARTNER" ? { phoneNumber: null } : getPhoneNumber(formData, patientPhoneNumber)
    const medgateConnectOrder: (CreateMedgateConnectOrderInput & UpdateMedgateConnectOrderInput) | undefined =
      medgateConnectChannel ? { birthDate: patientBirthDate } : undefined

    switch (mode.kind) {
      case "ADD_APPOINTMENT":
      case "FOLLOWUP_FOR_PROGRESS_NOTE": {
        const createAppointmentInput: CreateAppointmentInput = {
          channel: restFormData.channel,
          flexibility: restFormData.flexibility,
          language: restFormData.language,
          preventionProgramKeys: restFormData.preventionProgramKeys,
          reason: restFormData.reason,
          requestType: restFormData.requestType,
          service: restFormData.service,
          specialty: restFormData.specialty,
          patientId: countryPatient.patientId,
          date: dateInput,
          slotId: slotIdInput,
          urgency,
          appointmentType,
          assignedTo,
          desiredAgentGender,
          access: getAccess(isInContextOfAppointment, mode.data.callId),
          callerRole: mode.data.callerRole,
          inboundCallId: mode.data.inboundCallId,
          patientAddress,
          followUpForProgressNoteId: followUpForProgressNoteIdInput,
          telephoneLineKey: mode.data.telephoneLineKey,
          patientPhoneNumber: phoneNumber,
          productConfigurationAllowsToFavorNurse: mayFavorNurseWhenAssigningMedicalAgent(patient),
          medgateConnectChannel,
          partnerUserEmail: assignedPartner,
          medgateConnectOrder,
          isPartnerDoc2U,
          partnerType,
        }

        const connectionId = getPatientConnectionId(patientId)
        createAppointment(
          createAppointmentInput,
          [connectionId],
          (appointmentId: string) => {
            handleCloseDrawer()

            if (restFormData.service === "PREVENTION_ASSESSMENT" && restFormData.preventionProgramKeys) {
              savePreventionEngagements(restFormData.preventionProgramKeys)
            }

            if (sendRequestAfterSaving?.isSendRequestAfterSaving && sendRequestAfterSaving?.documentType)
              requestUploadFile({
                requestDocumentType: sendRequestAfterSaving.documentType,
                appointmentId,
                updater(store, data) {
                  try {
                    const appointmentRecordProxy = store.get(appointmentId)
                    const fileUploadRequest = data?.createFileUploadRequest.fileUploadRequest

                    if (fileUploadRequest?.id) {
                      const newRecordProxy = store.get(fileUploadRequest.id)
                      if (newRecordProxy) {
                        appointmentRecordProxy?.setLinkedRecord(newRecordProxy, "fileUploadRequest")
                      }
                    }
                  } catch (error) {
                    console.error("Error while processing data:", error)
                  }
                },
              })
          },
          handleBookingErrors,
        )
        break
      }
      case "EDITING_APPOINTMENT": {
        const updateAppointmentInput: UpdateAppointmentInput = {
          channel: restFormData.channel,
          flexibility: restFormData.flexibility,
          language: restFormData.language,
          preventionProgramKeys: restFormData.preventionProgramKeys,
          reason: restFormData.reason,
          requestType: restFormData.requestType,
          service: restFormData.service,
          specialty: restFormData.specialty,
          id: id!,
          date: dateInput,
          slotId: slotIdInput,
          urgency,
          appointmentType,
          assignedTo,
          desiredAgentGender,
          access: mode.data.appointment.access,
          patientAddress,
          followUpForProgressNoteId: followUpForProgressNoteIdInput,
          patientPhoneNumber: phoneNumber,
          productConfigurationAllowsToFavorNurse: mayFavorNurseWhenAssigningMedicalAgent(patient),
          medgateConnectChannel,
          partnerUserEmail: assignedPartner,
          isPartnerDoc2U,
          medgateConnectOrder,
          partnerType,
        }

        updateAppointment(
          updateAppointmentInput,
          mode.data.appointment.followUpForProgressNote?.id ?? "",
          () => {
            handleCloseDrawer()

            if (restFormData.service === "PREVENTION_ASSESSMENT" && restFormData.preventionProgramKeys) {
              savePreventionEngagements(restFormData.preventionProgramKeys)
            }
          },
          handleBookingErrors,
        )
        break
      }
    }
  }

  return (
    <Button
      disabled={isValidating}
      isLoading={isCreateFlight || isUpdateFlight}
      onClick={() => {
        FaroInsightsWrapper.pushEvent("follow_up_save")
        handleSubmit(handleSave)()
      }}
      data-testid="teleconsultation-drawer-save-button">
      {t("common.button.save")}
    </Button>
  )
}

export { SubmitButton }
